/**
 * User-configurable Internet Identity session duration (maxTimeToLive).
 * Stored locally; applied on next login.
 */

export const SESSION_TTL_KEY = "motokomaker.v2.sessionTtlHours";

export type SessionTtlOption = {
	hours: number;
	label: string;
	description: string;
};

export const SESSION_TTL_OPTIONS: SessionTtlOption[] = [
	{ hours: 1, label: "1 hour", description: "Short sessions on shared devices" },
	{ hours: 8, label: "8 hours", description: "Workday session" },
	{ hours: 24, label: "24 hours", description: "Daily reconnect" },
	{ hours: 168, label: "7 days", description: "Default long session" },
];

const DEFAULT_HOURS = 168;

export function getSessionTtlHours(): number {
	if (typeof localStorage === "undefined") return DEFAULT_HOURS;
	try {
		const raw = localStorage.getItem(SESSION_TTL_KEY);
		if (!raw) return DEFAULT_HOURS;
		const n = Number(raw);
		if (!Number.isFinite(n) || n <= 0) return DEFAULT_HOURS;
		const allowed = SESSION_TTL_OPTIONS.some((o) => o.hours === n);
		return allowed ? n : DEFAULT_HOURS;
	} catch {
		return DEFAULT_HOURS;
	}
}

export function setSessionTtlHours(hours: number): void {
	if (typeof localStorage === "undefined") return;
	const allowed = SESSION_TTL_OPTIONS.some((o) => o.hours === hours);
	localStorage.setItem(SESSION_TTL_KEY, String(allowed ? hours : DEFAULT_HOURS));
}

/** Nanoseconds for AuthClient.signIn maxTimeToLive. */
export function sessionTtlNs(): bigint {
	const hours = getSessionTtlHours();
	return BigInt(hours) * 60n * 60n * 1_000_000_000n;
}

export function sessionTtlLabel(): string {
	const hours = getSessionTtlHours();
	return (
		SESSION_TTL_OPTIONS.find((o) => o.hours === hours)?.label ?? "7 days"
	);
}
